import { isCalendarEventInFuture } from "../../utils/dateHelpers";
import { shouldWriteRemote, upsertClientApp, upsertEventApp, allOk } from "../../services/supabaseEntityWrites";
import { logger, toLogError } from "../../utils/logger";

/**
 * Clients + actions liées client/événement (annulation séance).
 * Écritures Supabase directes si coach distant ; sinon `persist()` (localStorage).
 */
export function createClientsSlice(set, get) {
  return {
    setClients: (fn) =>
      set((s) => {
        const next = typeof fn === "function" ? fn(s.clients) : fn;
        const sel = s.selectedClient;
        const synced = sel && Array.isArray(next) ? next.find((c) => c.id === sel.id) : sel;
        return { clients: next, selectedClient: synced ?? sel };
      }),

    selectClient: (c) =>
      set((s) => ({
        selectedClient: s.clients.find((cl) => cl.id === c.id) || c,
        view: "client-profile",
      })),

    archiveClient: async (clientId, opts = {}) => {
      const prevClients = get().clients;
      const prevEvents = get().events;
      const prevSel = get().selectedClient;
      const cancelledEventIds = prevEvents
        .filter((e) => e.clientId === clientId && e.status === "scheduled")
        .map((e) => e.id);

      set((s) => {
        const newEvents = s.events.map((e) =>
          e.clientId === clientId && e.status === "scheduled"
            ? { ...e, status: "cancelled", endingCancelled: true, ...(opts.billable ? { billable: true } : {}) }
            : e
        );
        const newClients = s.clients.map((c) => {
          if (c.id !== clientId) return c;
          const updated = { ...c, status: "archived" };
          if (opts.billable && opts.ghostSessions?.length) {
            updated.sessions = [...(c.sessions || []), ...opts.ghostSessions];
          }
          // Entrée de clôture pack/programme (D10)
          if (opts.closedPack) {
            updated.sessions = [
              ...(updated.sessions || c.sessions || []),
              {
                id: `pack-closed-${clientId}-${Date.now()}`,
                date: new Date().toLocaleDateString("fr-FR"),
                status: "pack_closed",
                duration: 0,
                summary: opts.closedPack.summary,
                closedPackValue: opts.closedPack.value,
                closedPackRemaining: opts.closedPack.remaining,
              },
            ];
          }
          return updated;
        });
        const sel =
          s.selectedClient?.id === clientId
            ? { ...s.selectedClient, status: "archived" }
            : s.selectedClient;
        return { clients: newClients, events: newEvents, selectedClient: sel };
      });

      if (!shouldWriteRemote(get().coachId)) {
        get().persist?.();
        return;
      }
      const coachId = get().coachId;
      const client = get().clients.find((c) => c.id === clientId);
      const eventResults = await Promise.all(
        cancelledEventIds.map((id) => {
          const e = get().events.find((x) => x.id === id);
          return e ? upsertEventApp(e, coachId) : Promise.resolve({ ok: true });
        })
      );
      const rClient = await upsertClientApp(client, coachId);
      if (!rClient.ok || !allOk(eventResults)) {
        set({ clients: prevClients, events: prevEvents, selectedClient: prevSel });
        logger.error("[archiveClient] remote write failed", toLogError(rClient.error), { clientId: id });
      }
    },

    reactivateClient: async (clientId) => {
      const prevClients = get().clients;
      const prevSel = get().selectedClient;

      set((s) => {
        const cleanClient = (c) => {
          if (c.id !== clientId) return c;
          const sessions = (c.sessions || []).filter(
            (sess) => !sess.id?.startsWith("cancel-end-") && !sess.id?.startsWith("cancel-custom-")
          );
          return { ...c, status: "active", archiveAfter: undefined, sessions };
        };
        return {
          clients: s.clients.map(cleanClient),
          selectedClient: s.selectedClient?.id === clientId
            ? cleanClient(s.selectedClient)
            : s.selectedClient,
        };
      });

      if (!shouldWriteRemote(get().coachId)) {
        get().persist?.();
        return;
      }
      const coachId = get().coachId;
      const client = get().clients.find((c) => c.id === clientId);
      const rClient = await upsertClientApp(client, coachId);
      if (!rClient.ok) {
        set({ clients: prevClients, selectedClient: prevSel });
        logger.error("[reactivateClient] remote write failed", toLogError(rClient.error), { clientId });
      }
    },

    standbyClient: async (clientId) => {
      const prevClients = get().clients;
      const prevEvents = get().events;
      const prevSel = get().selectedClient;
      const standbyIds = prevEvents
        .filter((e) => e.clientId === clientId && e.status === "scheduled")
        .map((e) => e.id);

      set((s) => {
        const newEvents = s.events.map((e) =>
          e.clientId === clientId && e.status === "scheduled"
            ? { ...e, status: "standby", standbyOriginClientId: clientId }
            : e
        );
        const patchClient = (c) =>
          c.id === clientId ? { ...c, status: "standby", standbyAt: new Date().toISOString() } : c;
        return {
          events: newEvents,
          clients: s.clients.map(patchClient),
          selectedClient: s.selectedClient?.id === clientId ? patchClient(s.selectedClient) : s.selectedClient,
        };
      });

      if (!shouldWriteRemote(get().coachId)) {
        get().persist?.();
        return;
      }
      const coachId = get().coachId;
      const client = get().clients.find((c) => c.id === clientId);
      const eventResults = await Promise.all(
        standbyIds.map((id) => {
          const e = get().events.find((x) => x.id === id);
          return e ? upsertEventApp(e, coachId) : Promise.resolve({ ok: true });
        })
      );
      const rClient = await upsertClientApp(client, coachId);
      if (!rClient.ok || !allOk(eventResults)) {
        set({ clients: prevClients, events: prevEvents, selectedClient: prevSel });
        logger.error("[standbyClient] remote write failed", toLogError(rClient.error), { clientId });
      }
    },

    reactivateStandbyClient: async (clientId) => {
      const prevClients = get().clients;
      const prevEvents = get().events;
      const prevSel = get().selectedClient;
      const candidateIds = prevEvents
        .filter((e) => e.standbyOriginClientId === clientId && e.status === "standby")
        .map((e) => e.id);

      set((s) => {
        const conflicts = [];
        const newEvents = s.events.map((e) => {
          if (e.standbyOriginClientId === clientId && e.status === "standby") {
            if (!isCalendarEventInFuture(e)) {
              return e;
            }
            const conflict = s.events.find(
              (o) =>
                o.id !== e.id &&
                o.status === "scheduled" &&
                (o.date && e.date ? o.date === e.date && o.time === e.time : o.day === e.day && o.time === e.time)
            );
            if (conflict) {
              conflicts.push({ event: e, conflictWith: conflict });
              return e;
            }
            return { ...e, status: "scheduled", standbyOriginClientId: undefined };
          }
          return e;
        });
        const patchClient = (c) =>
          c.id === clientId
            ? { ...c, status: "active", standbyAt: undefined, subscriptionMaintained: null }
            : c;
        return {
          events: newEvents,
          clients: s.clients.map(patchClient),
          selectedClient: s.selectedClient?.id === clientId ? patchClient(s.selectedClient) : s.selectedClient,
          _standbyConflicts: conflicts.length > 0 ? conflicts : null,
        };
      });

      if (!shouldWriteRemote(get().coachId)) {
        get().persist?.();
        return;
      }
      const coachId = get().coachId;
      const client = get().clients.find((c) => c.id === clientId);
      const eventResults = await Promise.all(
        candidateIds.map((id) => {
          const e = get().events.find((x) => x.id === id);
          return e ? upsertEventApp(e, coachId) : Promise.resolve({ ok: true });
        })
      );
      const rClient = await upsertClientApp(client, coachId);
      if (!rClient.ok || !allOk(eventResults)) {
        set({ clients: prevClients, events: prevEvents, selectedClient: prevSel });
        logger.error("[reactivateStandbyClient] remote write failed", toLogError(rClient.error), { clientId: id });
      }
    },

    updateClientField: async (clientId, field, value) => {
      set((s) => {
        const clients = s.clients.map((c) => (c.id === clientId ? { ...c, [field]: value } : c));
        const selectedClient =
          s.selectedClient?.id === clientId ? { ...s.selectedClient, [field]: value } : s.selectedClient;
        return { clients, selectedClient };
      });
      if (!shouldWriteRemote(get().coachId)) {
        get().persist?.();
        return;
      }
      const client = get().clients.find((c) => c.id === clientId);
      if (!client) return;
      const r = await upsertClientApp(client, get().coachId);
      if (!r.ok) {
        logger.error("[updateClientField] remote write failed", toLogError(r.error), { clientId, field });
      }
    },

    handleCancelEvent: async (ev, opts = {}) => {
      const prevClients = get().clients;
      const prevEvents = get().events;
      set((s) => {
        const newEvents = s.events.map((e) =>
          e.id === ev.id ? { ...e, status: "cancelled", ...(opts.billable ? { billable: true } : {}) } : e
        );
        const newClients = s.clients.map((c) => {
          if (c.id !== ev.clientId) return c;
          const updated = { ...c, cancelCount: (c.cancelCount || 0) + 1 };
          if (opts.billable && opts.date) {
            updated.sessions = [
              ...(c.sessions || []),
              {
                id: `cancel-${ev.id}`,
                date: opts.date,
                status: "cancelled_billable",
                duration: 0,
                summary: "Séance annulée (facturée)",
              },
            ];
          }
          return updated;
        });
        return { events: newEvents, clients: newClients };
      });

      if (!shouldWriteRemote(get().coachId)) {
        get().persist?.();
        return;
      }
      const coachId = get().coachId;
      const evAfter = get().events.find((e) => e.id === ev.id);
      const clientAfter = get().clients.find((c) => c.id === ev.clientId);
      const [rEv, rCl] = await Promise.all([
        upsertEventApp(evAfter, coachId),
        clientAfter ? upsertClientApp(clientAfter, coachId) : Promise.resolve({ ok: true }),
      ]);
      if (!rEv.ok || !rCl.ok) {
        set({ clients: prevClients, events: prevEvents });
        logger.error("[handleCancelEvent] remote write failed", toLogError(rEv.error || rCl.error));
      }
    },
  };
}
